import { FormEvent, useEffect, useState } from 'react';
import { Button, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';

import { LevelOfSkillDto } from 'utils/wemble-api.generated';
import {
  useDeleteLevelOfSkillMutation,
  useCreateLevelOfSkillMutation,
  useUpdateLevelOfSkillMutation,
} from 'utils/wemble-api';

const SkillModal = ({
  isOpen,
  onClose,
  title,
  data,
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  data?: LevelOfSkillDto | undefined;
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState();
  const [name, setName] = useState(data?.name ?? '');
  const [level, setLevel] = useState(data?.level?.toString() ?? '');
  const [removeLevelOfSkill] = useDeleteLevelOfSkillMutation();
  const [createLevelOfSkill] = useCreateLevelOfSkillMutation();
  const [updateLevelOfSkill] = useUpdateLevelOfSkillMutation();

  useEffect(() => {
    if (isOpen && data) {
      setName(data.name ?? '');
      setLevel(data.level?.toString() ?? '');
    }
  }, [isOpen]);

  const resetState = () => {
    setName('');
    setLevel('');
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleSubmit = async (ev: FormEvent) => {
    setIsSubmitting(true);
    setErrorMsg(undefined);
    ev.preventDefault();
    let res;
    if (data && data._id) {
      res = await updateLevelOfSkill({
        id: data._id,
        levelOfSkillUpsertParams: {
          name,
          level: parseInt(level, 10),
        },
      });
    } else {
      res = await createLevelOfSkill({
        levelOfSkillUpsertParams: {
          name,
          level: parseInt(level, 10),
        },
      });
    }
    if ('error' in res) {
      const error = res.error as any;
      if (typeof error?.data?.message === 'string') {
        setErrorMsg(error?.data?.message);
      }
      setIsSubmitting(false);
      return;
    }
    setIsSubmitting(false);
    handleClose();
  };

  const handleDelete = () => {
    setIsSubmitting(true);
    if (!data?._id) return;
    removeLevelOfSkill({
      id: data._id,
    });
    setIsSubmitting(false);
    handleClose();
  };

  return (
    <Modal isOpen={isOpen} toggle={handleClose}>
      <form onSubmit={handleSubmit}>
        <ModalHeader toggle={handleClose}>{title}</ModalHeader>
        <ModalBody>
          <table className={'table'}>
            <tbody>
              <tr>
                <th scope="row" className={'control-label'}>
                  Name
                </th>
                <td>
                  <input
                    id="skill-name"
                    placeholder='Example: "Expert"'
                    value={name}
                    type="text"
                    name="name"
                    required
                    className={'form-control'}
                    onChange={(ev) => setName(ev.target.value)}
                    disabled={isSubmitting}
                  />
                </td>
              </tr>
              <tr>
                <th scope="row" className={'control-label'}>
                  Level
                </th>
                <td>
                  <input
                    placeholder='Example: "1"'
                    value={level}
                    type="number"
                    name="level"
                    required
                    className={'form-control'}
                    onChange={(ev) => setLevel(ev.target.value)}
                    disabled={isSubmitting}
                  />
                </td>
              </tr>
            </tbody>
          </table>
          {errorMsg && <span className="text-danger">{errorMsg}</span>}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          {data && (
            <Button color="danger" onClick={handleDelete} disabled={isSubmitting}>
              Delete
            </Button>
          )}
          <Button color="primary" type="submit" disabled={isSubmitting}>
            {data ? 'Save' : 'Add'}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
};

export default SkillModal;
